import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CustomGroupListItem, SuggestionGroup } from '@metron/shared';
import { Permission } from '@metron/shared';
import { useCustomers } from '../hooks/useCustomers.ts';
import { useCustomGroups } from '../hooks/useCustomGroups.ts';
import { usePermissions } from '../hooks/usePermissions.ts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { Button } from '../components/ui/button.tsx';
import { CustomerPicker } from '../components/CustomerPicker.tsx';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { CustomGroupRow } from '../components/custom-group/CustomGroupRow.tsx';
import { toErrorMessage } from '@metron/shared';

export function CustomGroupsPage() {
  const { hasPermission } = usePermissions();
  const { customers, isLoading: customersLoading, error: customersError } = useCustomers();
  const {
    isLoading: _hookLoading,
    listCustomGroups,
    createCustomGroup,
    updateCustomGroup,
    deleteCustomGroup,
    getSensors,
    addSubmeters,
    removeSubmeter,
    addMasterSensors,
    removeMasterSensor,
    getCustomerSensors,
    getSuggestions,
    getSourceGroupSensors,
  } = useCustomGroups();

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [groups, setGroups] = useState<CustomGroupListItem[]>([]);
  const [newGroups, setNewGroups] = useState<Array<{ tempId: number }>>([]);
  const [suggestions, setSuggestions] = useState<SuggestionGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = hasPermission(Permission.EDIT_CUSTOM_GROUPS);
  const canDelete = hasPermission(Permission.DELETE_CUSTOM_GROUPS);

  const selectedCustomer = useMemo(() => {
    if (!selectedCustomerId) return null;
    return customers.find((c) => c.id === selectedCustomerId) ?? null;
  }, [customers, selectedCustomerId]);

  // Load custom groups when customer is selected
  const loadGroups = useCallback(async (customerId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listCustomGroups(customerId);
      setGroups(result.customGroups);
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to load custom groups');
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [listCustomGroups]);

  // Load suggestions when customer is selected
  const loadSuggestions = useCallback(async (customerId: number) => {
    try {
      const result = await getSuggestions(customerId);
      setSuggestions(result.suggestions);
    } catch {
      // Non-critical: suggestions are optional
      setSuggestions([]);
    }
  }, [getSuggestions]);

  useEffect(() => {
    if (selectedCustomerId) {
      loadGroups(selectedCustomerId);
      loadSuggestions(selectedCustomerId);
    } else {
      setGroups([]);
      setSuggestions([]);
    }
  }, [selectedCustomerId, loadGroups, loadSuggestions]);

  const handleCustomerChange = useCallback((id: number) => {
    setSelectedCustomerId(id);
    setNewGroups([]);
  }, []);

  const handleAddGroup = useCallback(() => {
    setNewGroups((prev) => [{ tempId: Date.now() }, ...prev]);
  }, []);

  const handleSaveNewGroup = useCallback(async (
    tempId: number,
    data: {
      name: string;
      description?: string;
      address?: string;
      sourceSensorGroupId?: number;
    },
  ) => {
    if (!selectedCustomerId) return;

    const result = await createCustomGroup({
      customerId: selectedCustomerId,
      name: data.name,
      description: data.description,
      address: data.address,
    });

    // Seed sensors from suggestion source group if selected
    if (data.sourceSensorGroupId && result.customGroup) {
      try {
        const sourceResult = await getSourceGroupSensors(data.sourceSensorGroupId);
        if (sourceResult.sensorIds.length > 0) {
          await addSubmeters(result.customGroup.id, {
            sensorIds: sourceResult.sensorIds,
          });
          toast.success(
            `Added ${sourceResult.sensorIds.length} sensor(s) from suggestion`,
          );
        }
      } catch (seedErr) {
        const msg = toErrorMessage(seedErr, 'Failed to seed sensors');
        toast.error(`Group created but failed to seed sensors: ${msg}`);
      }
    }

    // Remove from new groups list
    setNewGroups((prev) => prev.filter((g) => g.tempId !== tempId));

    // Reload groups to get the full list item with sensor counts
    await loadGroups(selectedCustomerId);

    return result;
  }, [
    selectedCustomerId,
    createCustomGroup,
    getSourceGroupSensors,
    addSubmeters,
    loadGroups,
  ]);

  const handleSaveExistingGroup = useCallback(async (
    groupId: number,
    data: { name: string; description?: string; address?: string },
  ) => {
    const result = await updateCustomGroup(groupId, {
      name: data.name,
      description: data.description ?? null,
      address: data.address ?? null,
    });

    // Refresh the list
    if (selectedCustomerId) {
      await loadGroups(selectedCustomerId);
    }

    return result;
  }, [updateCustomGroup, selectedCustomerId, loadGroups]);

  const handleDeleteGroup = useCallback(async (groupId: number) => {
    await deleteCustomGroup(groupId);
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, [deleteCustomGroup]);

  const handleCancelNew = useCallback((tempId: number) => {
    setNewGroups((prev) => prev.filter((g) => g.tempId !== tempId));
  }, []);

  const hookMethods = useMemo(() => ({
    getSensors,
    addSubmeters,
    addMasterSensors,
    removeSubmeter,
    removeMasterSensor,
    getCustomerSensors,
    getSourceGroupSensors,
  }), [
    getSensors,
    addSubmeters,
    addMasterSensors,
    removeSubmeter,
    removeMasterSensor,
    getCustomerSensors,
    getSourceGroupSensors,
  ]);

  if (customersLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-muted-foreground'>Loading...</p>
      </div>
    );
  }

  if (customersError) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-destructive'>Error: {customersError}</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Customer Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Custom Groups</CardTitle>
          <CardDescription>
            Create and manage custom meter groups for net consumption analysis.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-start gap-4'>
            <div className='flex-1 max-w-md'>
              <label htmlFor='customer-select' className='text-sm font-medium'>
                Customer
              </label>
              <CustomerPicker
                customers={customers}
                selectedCustomerId={selectedCustomerId}
                onSelect={handleCustomerChange}
                isLoading={customersLoading}
                searchFields={['name']}
                placeholder='Search customers by name…'
                inputId='customer-select'
              />
            </div>

            {selectedCustomerId && canEdit && (
              <Button onClick={handleAddGroup} variant='outline'>
                <Plus className='h-4 w-4 mr-2' />
                Add Custom Group
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Groups List */}
      {selectedCustomerId && (
        <Card>
          <CardHeader>
            <CardTitle>
              Groups{selectedCustomer ? ` - ${selectedCustomer.waterscopeAccountName}` : ''}
            </CardTitle>
            <CardDescription>
              {groups.length} custom group{groups.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading
              ? (
                <div className='flex items-center justify-center py-12'>
                  <p className='text-muted-foreground'>Loading custom groups...</p>
                </div>
              )
              : error
              ? (
                <div className='flex items-center justify-center py-12'>
                  <p className='text-destructive'>Error: {error}</p>
                </div>
              )
              : groups.length === 0 && newGroups.length === 0
              ? (
                <div className='flex flex-col items-center justify-center py-12 text-center'>
                  <p className='text-muted-foreground mb-4'>
                    No custom groups found for this customer.
                  </p>
                  {canEdit && (
                    <Button onClick={handleAddGroup} variant='outline'>
                      <Plus className='h-4 w-4 mr-2' />
                      Add First Custom Group
                    </Button>
                  )}
                </div>
              )
              : (
                <div className='space-y-4'>
                  {/* New (unsaved) groups */}
                  {newGroups.map((newGroup) => (
                    <CustomGroupRow
                      key={`new-${newGroup.tempId}`}
                      group={null}
                      customerId={selectedCustomerId}
                      isNew
                      onSave={(data) => handleSaveNewGroup(newGroup.tempId, data)}
                      onDelete={async () => {}}
                      onCancel={() => handleCancelNew(newGroup.tempId)}
                      useCustomGroupsHook={hookMethods}
                      suggestions={suggestions}
                      hasEditPermission={canEdit}
                      hasDeletePermission={canDelete}
                    />
                  ))}

                  {/* Existing groups */}
                  {groups.map((group) => (
                    <CustomGroupRow
                      key={group.id}
                      group={group}
                      customerId={selectedCustomerId}
                      isNew={false}
                      onSave={(data) => handleSaveExistingGroup(group.id, data)}
                      onDelete={handleDeleteGroup}
                      onCancel={() => {}}
                      useCustomGroupsHook={hookMethods}
                      hasEditPermission={canEdit}
                      hasDeletePermission={canDelete}
                    />
                  ))}
                </div>
              )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
